import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'

import type {
  OrderItemsModule, OrderItemsModuleApi, ReviewClosingBreakdownLine, ReviewSummaryLine,
} from '../../OrderForm/types'
import { rateioSellingPrice } from '../../OrderForm/utils/rateioSellingPrice'
import { computeReviewTotals } from '../../OrderForm/utils/reviewTotals'
import { OrderItemsProvider, useOrderItems } from './contexts/OrderItemsContext'
import { buildFashionBreakdown, buildFashionClosingRows } from './fashionClosing'
import { useOrderTotals } from './Hooks/useOrderTotals'
import { buildFashionItemsPayload } from './fashionItems.payload'
import type { FashionItemPayload } from './fashionItems.payload'
import { fashionOrderItemSchema } from './fashionOrder.schema'
import type { FashionOrderFormValues } from './fashionOrder.schema'
import ItemsSection from './Sections/Items'
import type { ProductApiData } from './types'
import { formatCurrency } from './utils'

interface FashionLoadedItems { products: ProductApiData[]; kitCount: number }

const useFashionItemsModule = (): OrderItemsModuleApi<FashionLoadedItems, FashionItemPayload> => {
  const { watch, setValue } = useFormContext<FashionOrderFormValues>()
  const {
    addedProducts, setAddedProducts, kitCount, setKitCount,
    setIsItemsSectionOpen, setOpenKits, setOpenProductIndex,
  } = useOrderItems()

  const shippingCost = watch('shippingCost')
  const totals = useOrderTotals({ addedProducts, shippingCost, setValue })

  const onSelectItem = useCallback((index: number) => {
    const product = addedProducts[index]
    setIsItemsSectionOpen(true)
    if (product?.kitIndex !== null && product?.kitIndex !== undefined) {
      setOpenKits((prev) => ({ ...prev, [product.kitIndex as number]: true }))
    }
    setOpenProductIndex(index)
  }, [addedProducts, setIsItemsSectionOpen, setOpenKits, setOpenProductIndex])

  const entries = addedProducts.map((product, index) => ({
    index,
    label: `${index + 1} · ${product.fashionProduct?.name || 'Item'}`,
    status: typeof product.subtotal === 'number' ? formatCurrency(product.subtotal) : '—',
  }))

  const closingRows = buildFashionClosingRows(addedProducts)
  const closingBreakdown = useCallback(
    (index: number): ReviewClosingBreakdownLine[] => buildFashionBreakdown(addedProducts[index]),
    [addedProducts],
  )

  const distributePrice = useCallback((total: number) => {
    const results = rateioSellingPrice(
      total,
      addedProducts.map((product) => ({
        itemPrice: product.subtotal ?? null,
        sellingPrice: product.sellingPrice ?? null,
      })),
    )
    setAddedProducts(addedProducts.map((product, index) => (
      results[index]?.rateado ? { ...product, sellingPrice: results[index].sellingPrice } : product
    )))
  }, [addedProducts, setAddedProducts])

  const review = computeReviewTotals(closingRows)
  const reviewSummary: ReviewSummaryLine[] = [
    { label: 'Itens do pedido', emphasis: true },
    { label: 'Conjuntos:', value: String(kitCount) },
    { label: 'Itens:', value: String(review.count) },
    { label: 'Peças:', value: review.pieces > 0 ? String(review.pieces) : '—' },
    { label: 'Total Cadastrado:', value: review.registeredTotal > 0 ? formatCurrency(review.registeredTotal) : '—' },
    { label: 'Total de Venda:', value: review.effectiveTotal > 0 ? formatCurrency(review.effectiveTotal) : '—' },
  ]

  return {
    totals: {
      quantity: totals.quantity,
      pieces: totals.pieces,
      subtotal: totals.subtotal,
      taxes: 0,
      practicedTotal: totals.total,
      canDistribute: true,
    },
    reviewSummary,
    closingRows,
    closingBreakdown,
    navigation: {
      itemsLabel: `2 · Itens (${entries.length})${entries.length > 0 ? ' ▾' : ''}`,
      entries,
      onSelectItem,
    },
    buildItemsPayload: () => buildFashionItemsPayload(addedProducts),
    validateBeforeSave: () => (
      addedProducts.length === 0
        ? { ok: false, code: 'no-items', message: 'O pedido deve ter pelo menos um item.' }
        : { ok: true }
    ),
    hydrate: ({ products, kitCount }) => {
      setAddedProducts(products)
      setKitCount(kitCount)
    },
    distributePrice,
  }
}

export const fashionItemsModule: OrderItemsModule<FashionLoadedItems, FashionItemPayload> = {
  itemsSchema: z.array(fashionOrderItemSchema),
  useModule: useFashionItemsModule,
  Slot: ItemsSection,
  Provider: OrderItemsProvider,
}
